import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Spinner } from '../ui/spinner';
import { Alert, AlertDescription } from '../ui/alert';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { ChevronDown, ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';

// Building blocks shared by the Postgres-backed campaign panels (VIP Release, Wednesday
// Freespins, Second Chance Freespins, Friday Loyalty Bonus, Issue Test Freespins): collapsible sections, an editable table with add/edit/delete, and the freespin
// games JSON editor.

// currency is varchar(10) in Postgres.
const MAX_CURRENCY_BYTES = 10;
const byteLength = (s) => new TextEncoder().encode(s).length;
export const isValidCurrency = (s) => {
  const c = s.trim();
  return c !== '' && !/\s/.test(c) && byteLength(c) <= MAX_CURRENCY_BYTES;
};

export const isPositiveInt = (s) => /^\d+$/.test(String(s).trim()) && Number(s) > 0;

// The backend's ceiling on any freespin count entered.
export const MAX_FREESPINS = 300;
export const isFreespinCount = (s) => isPositiveInt(s) && Number(s) <= MAX_FREESPINS;
export const FREESPIN_COUNT_HINT = `Freespin count must be a whole number from 1 to ${MAX_FREESPINS}.`;

// The backend's limits, kept here once for the panels' inline checks: domain.MaxWagering for every
// campaign, and the Issue Test Freespins ones (domain.MaxTestFreespins*).
export const MAX_WAGERING = 40;
export const MAX_TEST_BET_LEVEL = 10;
export const MAX_TEST_EMAIL = 100;
export const MAX_TEST_PLATFORM = 50;
export const MAX_TEST_TITLE = 50;

export const apiError = (err, fallback) => err?.response?.data?.error || fallback;

// What is wrong with the games text, or '' when it has the shape the campaigns read (and the
// backend checks): a list of 1-500 strings, each two non-blank parts joined by one colon. Only
// strings are expected, so parsing cannot round a number.
const MAX_GAMES = 500;
const isGame = (g) => typeof g === 'string' && g.split(':').length === 2 && g.split(':').every((p) => p.trim() !== '');
const jsonError = (text) => {
  if (!text.trim()) return 'Enter the games JSON.';
  let games;
  try {
    games = JSON.parse(text);
  } catch (e) {
    return e.message;
  }
  if (!Array.isArray(games) || games.length === 0) return 'Games must be a list, e.g. ["part1:part2"].';
  if (games.length > MAX_GAMES) return `Games can list at most ${MAX_GAMES} entries.`;
  const bad = games.find((g) => !isGame(g));
  return bad === undefined ? '' : `Each game must be a string like "part1:part2"; ${JSON.stringify(bad)} is not.`;
};
export const gamesJsonError = jsonError;

// The games JSON is edited as text and never parsed into values, so it is saved exactly as typed;
// a row stored before the shape check may still hold numbers, which JavaScript would round above
// 2^53. So the formatter works on the tokens as written.
//
// jsonTokens splits JSON text that JSON.parse accepts into its strings, numbers, literals and
// punctuation, verbatim.
const jsonTokens = (text) => text.match(/"(?:[^"\\]|\\.)*"|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|true|false|null|[{}[\],:]/g) || [];

// indentTokens lays tokens out as JSON.stringify(value, null, 2) would.
const indentTokens = (tokens) => {
  let out = '';
  let depth = 0;
  const newline = () => `\n${'  '.repeat(depth)}`;
  tokens.forEach((t, i) => {
    const next = tokens[i + 1];
    if (t === '{' || t === '[') {
      depth += 1;
      out += t + (next === '}' || next === ']' ? '' : newline());
    } else if (t === '}' || t === ']') {
      depth -= 1;
      const prev = tokens[i - 1];
      out += (prev === '{' || prev === '[' ? '' : newline()) + t;
    } else if (t === ',') {
      out += `,${newline()}`;
    } else if (t === ':') {
      out += ': ';
    } else {
      out += t;
    }
  });
  return out;
};

// formatJson lays valid JSON text out for the small editor: a top-level array one compact item
// per line, anything else indented normally. Invalid text comes back as it is.
const formatJson = (text) => {
  if (!text.trim() || jsonError(text)) return text;
  const tokens = jsonTokens(text);
  if (tokens[0] !== '[' || tokens[1] === ']') return indentTokens(tokens);
  const items = [];
  let item = [];
  let depth = 0;
  tokens.slice(1, -1).forEach((t) => {
    if (t === ',' && depth === 0) {
      items.push(item);
      item = [];
      return;
    }
    if (t === '{' || t === '[') depth += 1;
    if (t === '}' || t === ']') depth -= 1;
    item.push(t);
  });
  items.push(item);
  return `[\n${items.map((it) => `  ${it.join('')}`).join(',\n')}\n]`;
};

// numeric(38, 20) amounts: up to 18 integer and 20 fractional digits, never negative.
export const isValidAmount = (s) => /^\d{1,18}(\.\d{1,20})?$/.test(s.trim());

export const FieldLabel = ({ children }) => (
  <span className="font-mono text-[10px] uppercase tracking-wider text-ink-mute">{children}</span>
);

// A titled section that can be minimized from its header. `actions` sit on the right of the
// header and hide with the content, as does the optional `description` under the title. It opens
// minimized when `defaultCollapsed` is set, each time its panel is opened.
export function CollapsibleSection({ title, description, defaultCollapsed, actions, children }) {
  const [collapsed, setCollapsed] = useState(Boolean(defaultCollapsed));
  const toggle = () => setCollapsed((c) => !c);
  const Chevron = collapsed ? ChevronRight : ChevronDown;

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={toggle} aria-expanded={!collapsed}
          className="flex items-center gap-1.5 -ml-1 px-1 rounded text-ink hover:text-ink-dim focus:outline-none focus-visible:ring-1 focus-visible:ring-cta">
          <Chevron className="h-4 w-4 text-ink-mute" />
          <h3 className="text-base font-bold">{title}</h3>
        </button>
        {!collapsed && actions}
      </div>
      {!collapsed && description && <p className="-mt-2 text-xs font-medium text-ink-dim">{description}</p>}
      {!collapsed && children}
    </section>
  );
}

// Edits one freespin games JSON. `value` is the stored games as exact text (the API's
// games_text), '' when there is no row yet; onSave gets the edited text and resolves once it is
// stored. A new `value` with the same text leaves an unsaved edit alone.
export function GamesJsonEditor({ value, onSave, disabled }) {
  const [saved, setSaved] = useState('');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    const t = formatJson(value || '');
    setSaved(t);
    setText(t);
  }, [value]);

  const parseError = jsonError(text);
  const dirty = text !== saved;

  const save = async () => {
    setSaving(true);
    setError('');
    setJustSaved(false);
    try {
      await onSave(text.trim());
      setJustSaved(true);
    } catch (err) {
      setError(apiError(err, 'Failed to save games.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <Textarea rows={5} spellCheck={false} className="min-h-0 font-mono text-xs" value={text}
        placeholder='["part1:part2"]' disabled={disabled}
        onChange={(e) => { setText(e.target.value); setJustSaved(false); }} />
      {dirty && parseError && <p className="text-xs font-medium text-negative">{parseError}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={save} disabled={disabled || !dirty || Boolean(parseError) || saving}>
          {saving ? 'Saving…' : 'Save games'}
        </Button>
        <Button variant="outline" size="sm" disabled={disabled || Boolean(parseError) || saving}
          onClick={() => setText(formatJson(text))}>
          Format
        </Button>
        {dirty && (
          <Button variant="outline" size="sm" onClick={() => setText(saved)} disabled={saving}>Reset</Button>
        )}
        {justSaved && !dirty && <span className="text-xs font-medium text-ink-mute">Saved.</span>}
      </div>
      {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
    </div>
  );
}

// The games section of a single-row freespin campaign (VIP Release, Second Chance): its one row
// in the shared freespin games table, of which only the games JSON is edited. `load` resolves to
// {games: row | null}; `save` takes the games JSON text and resolves to the stored row.
export function SingleGamesSection({ load, save }) {
  const [games, setGames] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await load();
        if (!res.data || typeof res.data !== 'object') throw new Error('unexpected response');
        setGames(res.data.games?.games_text ?? '');
      } catch (err) {
        setError(apiError(err, 'Failed to load games.'));
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const onSave = async (value) => {
    const res = await save(value);
    setGames(res.data.games_text);
  };

  return (
    <CollapsibleSection title="Games">
      <Card>
        <CardContent className="p-5 space-y-4">
          {loading ? (
            <div className="flex justify-center py-4"><Spinner /></div>
          ) : (
            !error && <GamesJsonEditor value={games} onSave={onSave} />
          )}
          {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
        </CardContent>
      </Card>
    </CollapsibleSection>
  );
}

// An add/edit dialog driven by a field list, for tables whose rows are a handful of inputs.
// Each field is {key, label, inputMode?, placeholder?, lockedOnEdit?, options?, initial?,
// disabled?}; its value starts as the edited row's `key` (as text), else `initial`, else empty.
// A field with `options` ([{value, label}]) is a select; `disabled(values)` greys a field out and
// clears it, e.g. one that only applies for some choice of another. `validate(values)` returns '' when the values can be
// saved, else the reason (shown once something has been typed); `submit(values, initial)` saves,
// `initial` being the edited row or null when adding.
export function RecordFormDialog({ open, initial, title, description, fields, validate, submit, submitLabel, onClose, onSaved }) {
  const editing = Boolean(initial);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setValues(Object.fromEntries(fields.map((f) => [f.key, initial?.[f.key] != null ? String(initial[f.key]) : (f.initial ?? '')])));
    setError('');
  }, [open, initial, fields]);

  const set = (key, value) => setValues((v) => {
    const next = { ...v, [key]: value };
    // Clear what the change just made inapplicable, so it is neither shown nor sent.
    fields.forEach((f) => { if (f.disabled?.(next)) next[f.key] = ''; });
    return next;
  });

  const problem = validate(values);
  const typed = fields.some((f) => !f.options && (values[f.key] ?? '').trim() !== '');

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await submit(values, initial);
      onSaved();
    } catch (err) {
      setError(apiError(err, 'Failed to save.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${title}` : `Add ${title}`}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            {fields.map((f) => {
              const disabled = (editing && f.lockedOnEdit) || Boolean(f.disabled?.(values));
              return f.options ? (
                <div key={f.key} className="space-y-1.5">
                  <FieldLabel>{f.label}</FieldLabel>
                  <Select value={values[f.key] ?? ''} onValueChange={(v) => set(f.key, v)} disabled={disabled}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <label key={f.key} className="space-y-1.5 block">
                  <FieldLabel>{f.label}</FieldLabel>
                  <Input inputMode={f.inputMode} placeholder={f.placeholder} value={values[f.key] ?? ''}
                    disabled={disabled} onChange={(e) => set(f.key, e.target.value)} />
                </label>
              );
            })}
          </div>
          {typed && problem && <p className="text-xs font-medium text-negative">{problem}</p>}
          {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={Boolean(problem) || saving}>
            {saving ? 'Saving…' : editing ? 'Save changes' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteDialog({ open, title, description, onConfirm, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setError(''); }, [open]);

  const confirm = async () => {
    setDeleting(true);
    try {
      await onConfirm();
      onDeleted();
    } catch (err) {
      setError(apiError(err, 'Failed to delete.'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description} This cannot be undone.</DialogDescription>
        </DialogHeader>
        {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>Cancel</Button>
          <Button variant="destructive" onClick={confirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Frame for a campaign's editable table: collapsible title, add button, loading/empty states, and the edit/delete
// buttons on each row.
export function EditableTable({ title, description, defaultCollapsed, addLabel, emptyText, loading, error, rows, rowKey, columns, onAdd, onEdit, onDelete }) {
  return (
    <CollapsibleSection title={title} description={description} defaultCollapsed={defaultCollapsed} actions={(
      <Button size="sm" onClick={onAdd}>
        <Plus className="h-3.5 w-3.5 mr-1.5" /> {addLabel}
      </Button>
    )}>

      {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : rows.length === 0 ? (
            <p className="py-10 text-center text-sm font-medium text-ink-dim">{emptyText}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((c) => <TableHead key={c.label} className={c.className}>{c.label}</TableHead>)}
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={rowKey(row)}>
                    {columns.map((c) => (
                      <TableCell key={c.label} className={`font-mono text-xs ${c.className || ''}`}>{c.render(row)}</TableCell>
                    ))}
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => onEdit(row)} aria-label="Edit">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => onDelete(row)} aria-label="Delete">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </CollapsibleSection>
  );
}

// Loads one list endpoint and tracks the add/edit/delete dialogs over it. `rows` is the
// response's `field`; `data` the whole response, for anything sent alongside the rows.
export function useEditableList(fetchList, field) {
  const [rows, setRows] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ open: false, row: null });
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchList();
      if (!Array.isArray(res.data?.[field])) throw new Error('unexpected response');
      setRows(res.data[field]);
      setData(res.data);
    } catch (err) {
      setError(apiError(err, 'Failed to load.'));
    } finally {
      setLoading(false);
    }
  }, [fetchList, field]);

  useEffect(() => { load(); }, [load]);

  return {
    rows, data, loading, error, form, deleting, load,
    openAdd: () => setForm({ open: true, row: null }),
    openEdit: (row) => setForm({ open: true, row }),
    closeForm: () => setForm({ open: false, row: null }),
    setDeleting,
  };
}
